import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { powerMethodTargetId } from '../AreaOverview/PowerOnboarding';
import { collectPowerObservations, confirmedTvMethods, discoveredBehaviour, discoveryProfile, observationFor } from './powerDiscovery';

const labels = { follows: 'Follows the TV power action', 'stays-awake': 'Stayed awake', 'already-awake': 'Already awake — startup from sleep untested',
  'needs-command': 'Stayed asleep — needs a startup command', unknown: 'Unknown — no confirmed transition' };
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function PowerDiscovery({ areaId, screen, devices, onSave, onBusyChange, observationMs = 20000 }) {
  const windowMs = observationMs;
  const [allOn, setAllOn] = useState(false);
  const [recoveryReady, setRecoveryReady] = useState(false);
  const [manualOff, setManualOff] = useState(false);
  const [manualOn, setManualOn] = useState(false);
  const [targetAsleep, setTargetAsleep] = useState(false);
  const [results, setResults] = useState({});
  const [stage, setStage] = useState('ready');
  const [message, setMessage] = useState('');
  const active = useRef(true);
  const running = useRef(false);
  const base = `/api/areas/${areaId}/topology/screens/${screen.id}`;
  const config = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
  const methods = { startup: confirmedTvMethods(screen, 'startup'), shutdown: confirmedTvMethods(screen, 'shutdown') };
  const selected = (phase) => methods[phase][0];
  const name = (id) => devices.find((device) => Number(device.id) === Number(id))?.name || `Device ${id}`;
  const playerIds = [...new Set((screen.players || []).map((player) => Number(player.deviceId || player.id)))];
  const busy = ['shutdown', 'startup', 'saving'].includes(stage);
  useEffect(() => () => { active.current = false; }, []);
  const assertActive = () => { if (!active.current) throw new Error('Discovery closed. No further commands sent.'); };
  const observe = async () => {
    const response = await axios.get(`${base}/power-observations`, config());
    assertActive();
    return response.data;
  };
  const test = async (phase, before, confirmedTarget) => {
    assertActive();
    const method = selected(phase);
    const targetId = powerMethodTargetId(method, screen);
    const expected = phase === 'shutdown' ? 'awake' : 'asleep';
    const tvState = observationFor(before, screen.televisionDeviceId)?.state;
    if (['awake', 'asleep'].includes(tvState) && tvState !== expected) {
      throw new Error(`The TV reports ${tvState}. Restore it to ${expected} before testing ${phase}.`);
    }
    const targetState = observationFor(before, targetId)?.state;
    if (method.type === 'android_tv_power_toggle' && ['awake', 'asleep'].includes(targetState) && targetState !== expected) {
      throw new Error(`${name(targetId)} reports ${targetState}; no Power toggle was sent.`);
    }
    if (method.type === 'android_tv_power_toggle' && observationFor(before, targetId)?.state !== expected && !confirmedTarget) {
      throw new Error(`Confirm ${name(targetId)} is ${expected} before testing its Power button.`);
    }
    setStage(phase);
    setMessage(`${phase === 'shutdown' ? 'Turning the TV off' : 'Starting the TV'} once, then observing connected boxes for ${windowMs / 1000} seconds…`);
    const response = await axios.post(`${base}/power-methods/test`, { phase, methodId: method.id,
      confirmCurrentState: phase === 'shutdown' ? 'on' : 'off' }, config());
    assertActive();
    const result = response.data;
    if (!result?.accepted || result.commandAcknowledged === false || result.packetEmitted === false || result.error) {
      throw new Error('The command was not confirmed. No automatic retry was sent. Check the TV before continuing.');
    }
    let after = {};
    const startedAt = result.startedAt || new Date().toISOString();
    const deadline = Date.now() + windowMs;
    do {
      const snapshot = await observe();
      after = collectPowerObservations(after, snapshot, startedAt, before);
      setResults((current) => ({ ...current, [phase]: { method, before, after } }));
      if (Date.now() >= deadline) break;
      await sleep(Math.min(2000, deadline - Date.now()));
      assertActive();
    } while (true);
    return { method, before, after };
  };
  const perform = async (action, failureStage = 'interrupted') => {
    if (running.current) return;
    running.current = true;
    onBusyChange(true);
    try { await action(); }
    catch (error) {
      if (active.current) { setStage(failureStage); setMessage(`${error.response?.data?.message || error.message}${failureStage === 'interrupted' ? ' Check the devices and restore them manually before starting a new test.' : ' Observations are retained; try saving again.'}`); }
    } finally {
      running.current = false;
      if (active.current) onBusyChange(false);
    }
  };
  const finishStartup = async (before, confirmedTarget, shutdownResult = results.shutdown) => {
    // A box may report standby and then disconnect. Preserve that observed baseline
    // within this attended cycle; do not use it to authorize a stale power toggle.
    const baseline = { ...before, devices: before.devices.map((device) => {
      const slept = shutdownResult?.after[device.deviceId];
      return device.state === 'unknown' && slept?.state === 'asleep' && Number(device.deviceId) !== Number(screen.televisionDeviceId)
        ? slept : device;
    }) };
    if (selected('startup').type === 'android_tv_power_toggle' && !confirmedTarget
      && observationFor(before, powerMethodTargetId(selected('startup'), screen))?.state !== 'asleep') {
      throw new Error('The power-button target has no fresh standby report. Confirm its state before testing again.');
    }
    await test('startup', baseline, confirmedTarget);
    setStage('review');
    setMessage('Review the observations below. Saving updates the connected device behaviour in the saved routine.');
  };
  const start = () => perform(async () => {
    setResults({}); setAllOn(false); setManualOff(false); setManualOn(false); setTargetAsleep(false);
    const before = await observe();
    const down = await test('shutdown', before, allOn);
    const current = await observe();
    const startupTarget = powerMethodTargetId(selected('startup'), screen);
    // Only continue automatically with a fresh, explicit TV standby report.
    if (down.after[screen.televisionDeviceId]?.state === 'asleep'
      && observationFor(current, screen.televisionDeviceId)?.state === 'asleep'
      && (selected('startup').type !== 'android_tv_power_toggle' || observationFor(current, startupTarget)?.state === 'asleep')) {
      await finishStartup(current, false, down);
    } else {
      setStage('awaiting-startup');
      setMessage('Automatic startup paused because the TV or command target has no fresh standby report. Confirm its state below; no second command has been sent.');
    }
  });
  const continueStartup = () => perform(async () => {
    const before = await observe();
    await finishStartup(before, targetAsleep || (powerMethodTargetId(selected('startup'), screen) === Number(screen.televisionDeviceId) && manualOff));
  });
  const save = () => perform(async () => {
    setStage('saving');
    await onSave(discoveryProfile(screen, results));
    assertActive();
    setStage('saved'); setMessage('Discovered behaviour saved to Startup and Shutdown.');
  }, 'review');
  const relayConflicts = playerIds.filter((id) => screen.activationStrategy?.deviceLifecycleProfile?.devices?.[id]?.recoveryAnchor
    && discoveredBehaviour(observationFor(results.shutdown?.before, id), results.shutdown?.after[id], 'shutdown') === 'follows');
  const tvOff = results.shutdown?.after[screen.televisionDeviceId]?.state === 'asleep' || manualOff;
  const tvOn = results.startup?.after[screen.televisionDeviceId]?.state === 'awake' || manualOn;
  return <section className="power-discovery" aria-label="Discover connected box behaviour">
    <h3>Discover Startup and Shutdown</h3>
    <p>Suitestream shuts down the TV once, records what each connected device does, then starts the TV and records the result. Repeat this after changing HDMI-CEC or device settings.</p>
    {(!selected('startup') || !selected('shutdown')) && <p role="status">First save and test a TV startup and shutdown method in Edit saved routine. Discovery will use the first confirmed method in each direction.</p>}
    {['ready', 'interrupted', 'saved'].includes(stage) && <fieldset disabled={busy}>
      <label><input type="checkbox" checked={allOn} onChange={(event) => setAllOn(event.target.checked)} /> The TV and every connected box are on.</label>
      <label><input type="checkbox" checked={recoveryReady} onChange={(event) => setRecoveryReady(event.target.checked)} /> I can restore power with a physical remote if the recovery relay also sleeps.</label>
      <button type="button" disabled={!allOn || !recoveryReady || !selected('startup') || !selected('shutdown')} onClick={start}>Discover Startup and Shutdown</button>
    </fieldset>}
    {stage === 'awaiting-startup' && <div>
      <label><input type="checkbox" checked={manualOff} onChange={(event) => setManualOff(event.target.checked)} /> I confirm the TV is off. Leave sleeping boxes asleep for the startup test.</label>
      {selected('startup')?.type === 'android_tv_power_toggle' && powerMethodTargetId(selected('startup'), screen) !== Number(screen.televisionDeviceId) &&
        <label><input type="checkbox" checked={targetAsleep} onChange={(event) => setTargetAsleep(event.target.checked)} /> I confirm {name(powerMethodTargetId(selected('startup'), screen))} is asleep before sending its Power button.</label>}
      <button type="button" disabled={!manualOff || (selected('startup')?.type === 'android_tv_power_toggle' && powerMethodTargetId(selected('startup'), screen) !== Number(screen.televisionDeviceId) && !targetAsleep)} onClick={continueStartup}>Start TV and observe boxes</button>
    </div>}
    {Object.keys(results).length > 0 && <div className="power-discovery__results">
      {playerIds.map((id) => <article key={id}><h4>{name(id)}</h4>{['shutdown', 'startup'].map((phase) => <p key={phase}>
        <strong>{phase === 'startup' ? 'Startup' : 'Shutdown'}: </strong>{results[phase] ? labels[discoveredBehaviour(observationFor(results[phase].before, id), results[phase].after[id], phase)] : 'Not tested'}
      </p>)}</article>)}
    </div>}
    {relayConflicts.length > 0 && <p role="alert">{relayConflicts.map(name).join(', ')} sleeps with the TV but is marked as a recovery relay. Keep an independent relay awake, or disable the relevant TV CEC power behaviour and train individual box shutdown commands. Retest before relying on remote startup.</p>}
    {stage === 'review' && <div>
      {!tvOff && <label><input type="checkbox" checked={manualOff} onChange={(event) => setManualOff(event.target.checked)} /> I observed the TV turn off during this test.</label>}
      {!tvOn && <label><input type="checkbox" checked={manualOn} onChange={(event) => setManualOn(event.target.checked)} /> I observed the TV turn on during this test.</label>}
      <button type="button" disabled={!tvOff || !tvOn} onClick={save}>Save discovered behaviour</button>
    </div>}
    {message && <p role="status">{message}</p>}
  </section>;
}
